import type { Dispatch, SetStateAction } from 'react';
import { PreparedForStep } from '../steps/prepared-for';
import { PreparedByStep } from '../steps/prepared-by';
import type { UserData } from '../steps/prepared-by';
import { ExpirationDateStep } from '../steps/expiration-date';
import { OverallNoteStep } from '../steps/overall-note';
import QuoteDisplay from '../steps/quote-display';
import { isValidEmail } from '../utils';
import type { QuoteDetailsFormData } from '../types';
import { QuoteDetailsSteps } from '../types';

interface StepContentRendererProps {
  activeStep: QuoteDetailsSteps;
  formData: QuoteDetailsFormData;
  currentPreparedData: UserData;
  isExpired: boolean;
  enableValidityDate: boolean;
  updateFormData: (stepId: string, data: any) => void;
  setFormData: Dispatch<SetStateAction<QuoteDetailsFormData>>;
  setIsExpired: (val: boolean) => void;
}

export const StepContentRenderer = ({
  activeStep,
  formData,
  currentPreparedData,
  isExpired,
  enableValidityDate,
  updateFormData,
  setFormData,
  setIsExpired
}: StepContentRendererProps) => {
  switch (activeStep) {
    case QuoteDetailsSteps.PREPARED_FOR:
      return (
        <PreparedForStep
          data={formData.preparedFor}
          onChange={data => updateFormData('preparedFor', data)}
        />
      );
    case QuoteDetailsSteps.PREPARED_BY:
      return (
        <PreparedByStep
          data={formData.preparedBy}
          onChange={data => updateFormData('preparedBy', data)}
          onImageUpload={(file: File) =>
            setFormData(prev => ({
              ...prev,
              preparedBy: {
                ...prev?.preparedBy,
                file
              }
            }))
          }
          emailError={
            currentPreparedData?.email &&
            !isValidEmail(currentPreparedData.email)
              ? 'Please enter a valid email address'
              : ''
          }
        />
      );
    case QuoteDetailsSteps.EXPIRATION_DATE:
      return (
        <ExpirationDateStep
          helperText={
            isExpired
              ? 'The expiration date has passed. Extend the quote to continue.'
              : null
          }
          disabled={isExpired && !enableValidityDate}
          error={isExpired && enableValidityDate}
          date={formData.expirationDate}
          onChange={date => {
            updateFormData('expirationDate', date);
            if (isExpired) setIsExpired(false);
          }}
        />
      );
    case QuoteDetailsSteps.OVERALL_NOTE:
      return (
        <OverallNoteStep
          note={formData.overallNote}
          onChange={note => updateFormData('overallNote', note)}
        />
      );
    case QuoteDetailsSteps.QUOTE_DISPLAY:
      return (
        <QuoteDisplay
          data={formData.quoteDisplay}
          onChange={data => updateFormData('quoteDisplay', data)}
        />
      );
    default:
      return null;
  }
};
